import React from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Truck, ArrowRight, Calendar, AlertCircle, ExternalLink } from "lucide-react";
import { useGuestContext } from "@/context/GuestContext";
import type { ReturnEntry } from "@/types";

const TrackStatus: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: orderIdParam } = useParams<{ id: string }>();
  const { returnOnboardingData, fetchOnboardingData } = useGuestContext();

  const state = location.state as {
    product?: { image: string; title: string; reason: string };
    orderId?: string;
    replacementProduct?: { image?: string; title?: string; color?: string; size?: string };
  } | null;

  const product = state?.product;
  const orderId = state?.orderId || orderIdParam;
  const replacementProduct = state?.replacementProduct;

  React.useEffect(() => {
    if (orderId && !returnOnboardingData) {
      fetchOnboardingData(orderId);
    }
  }, [orderId, returnOnboardingData, fetchOnboardingData]);

  const shipping = returnOnboardingData?.shipping_details;
  const orderDetails = returnOnboardingData?.order_details;

  const goBack = () => {
    if (orderId) {
      fetchOnboardingData(orderId, true);
    }
    navigate(`/customer/orders/${orderId}`);
  };


  // ── Mapping DB Timeline to UI ──
  const searchParams = new URLSearchParams(location.search);
  const productIdFromQuery = searchParams.get("productId");

  const returns = returnOnboardingData?.returns || [];
  
  const filteredReturns = productIdFromQuery 
    ? returns.filter((r: ReturnEntry) => r.product_id === productIdFromQuery)
    : returns;

  const latestReturn = filteredReturns.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0] as ReturnEntry | undefined;
  const dbTimeline = latestReturn?.timeline || [];
  
  const formatTime = (ts: string) => {
    return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' +
           new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const statusMap: Record<string, string> = {
    "Return Request Created": "Return Initiated",
    "Return Approved by AI": "AI Verification Complete",
    "processing": "Return Processing",
    "Pickup Scheduled": "Courier Assigned",
    "requested": "Return Requested",
    "approved": "Return Approved",
    "shipped": "Return Shipped",
    "delivered": "Return Delivered",
    "closed": "Return Completed",
    "completed": "Item Picked Up",
    "failed": "Pickup Attempt Failed",
    "trigger_reorder": "Replacement Dispatched",
    "trigger_refund": "Refund Processed"
  };

  const TrackingPanel = () => {
    // Deduplicate timeline: only keep the LATEST entry for each unique status
    const seenStatuses = new Set<string>();
    const uniqueTimeline = [...dbTimeline]
      .reverse()
      .filter((ev) => {
        let statusStr = ev.status;
        if (statusStr.startsWith("Shipment Status: ")) {
          statusStr = statusStr.replace("Shipment Status: ", "");
        }
        const label = statusMap[statusStr] || statusStr;
        if (seenStatuses.has(label)) return false;
        seenStatuses.add(label);
        return true;
      })
      .reverse();

    const displaySteps = uniqueTimeline.length > 0 
      ? uniqueTimeline.map((ev, idx) => {
          let statusStr = ev.status;
          if (statusStr.startsWith("Shipment Status: ")) {
            statusStr = statusStr.replace("Shipment Status: ", "");
          }
          return {
            label: statusMap[statusStr] || statusStr.charAt(0).toUpperCase() + statusStr.slice(1),
            sub: formatTime(ev.timestamp),
            done: idx < uniqueTimeline.length - 1,
            active: idx === uniqueTimeline.length - 1
          };
        })
      : [
          { label: "Return Initiated", sub: "Awaiting AI evaluation", done: false, active: true },
          { label: "AI Evaluation", sub: "Pending", done: false, active: false },
          { label: "Resolution Selection", sub: "Pending", done: false, active: false },
          { label: "Pickup Scheduled", sub: "Pending", done: false, active: false },
        ];

    return (
      <div className="tw-w-full lg:tw-w-72 xl:tw-w-80 tw-h-fit">
        <div className="tw-rounded-3xl tw-p-6 tw-border tw-border-white/5 tw-shadow-2xl" style={{ background: "#0F0F0F" }}>
          <h3 className="tw-text-white tw-font-bold tw-text-lg tw-mb-8 tw-flex tw-items-center tw-gap-2">
            <div className="tw-w-1.5 tw-h-5 tw-bg-[#00C5FB] tw-rounded-full" />
            Tracking Progress
          </h3>
          <div className="tw-flex tw-flex-col tw-space-y-0">
            {displaySteps.map((step, i) => (
              <div key={i} className="tw-flex tw-gap-5 tw-relative tw-pb-10 last:tw-pb-0">
                {i < displaySteps.length - 1 && (
                  <div className={`tw-absolute tw-left-[15px] tw-top-8 tw-bottom-0 tw-w-[1px] ${step.done ? "tw-bg-[#00C5FB]" : "tw-bg-gray-800"}`} />
                )}
                <div
                  className={`tw-relative tw-z-10 tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-border-2 ${
                    step.done ? "tw-bg-[#00C5FB] tw-border-[#00C5FB]" : step.active ? "tw-border-[#00C5FB] tw-bg-[#0F0F0F]" : "tw-border-gray-800 tw-bg-[#0F0F0F]"
                  }`}
                  style={step.done || step.active ? { boxShadow: "0 0 15px rgba(0, 197, 251, 0.3)" } : {}}
                >
                  {step.done ? (
                    <Check className="tw-w-4 tw-h-4 tw-text-black" strokeWidth={3} />
                  ) : step.active ? (
                    <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-[#00C5FB] tw-animate-pulse" />
                  ) : (
                    <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-gray-800" />
                  )}
                </div>
                <div className="tw-pt-0.5">
                  <p className={`tw-text-[15px] tw-font-bold tw-tracking-tight ${step.done || step.active ? "tw-text-white" : "tw-text-gray-600"}`}>
                    {step.label}
                  </p>
                  <p className={`tw-text-xs tw-mt-1.5 tw-font-medium ${step.active ? "tw-text-[#00C5FB]" : "tw-text-gray-500"}`}>
                    {step.sub}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── Unified Data Extraction ──
  // 1. Get the original product info based on the latest return's product_id
  const productInfoFromReturn = (latestReturn as any)?.product_id 
    ? returnOnboardingData?.product_info?.find((p: any) => p.id === (latestReturn as any).product_id)
    : returnOnboardingData?.product_info?.[0];

  const originalProduct = {
    image: product?.image || productInfoFromReturn?.image,
    title: product?.title || productInfoFromReturn?.title || "Product",
    reason: product?.reason || latestReturn?.reason || "Defective",
    variant: productInfoFromReturn?.variant || "",
  };

  // 2. Get the replacement product info
  // Prioritize: Location State > DB replacement_variant_id > DB is_replacement flag
  const replacementInfoFromReturn = ((latestReturn as any)?.replacement_variant_id 
    ? returnOnboardingData?.product_info?.find((p: any) => String(p.variant_id) === String((latestReturn as any).replacement_variant_id))
    : null) || returnOnboardingData?.product_info?.find((p: any) => (p as any).is_replacement);


  // Parse variants safely
  const parseVariant = (v?: string) => {
    if (!v) return { color: "", size: "" };
    const parts = v.split(' / ');
    if (parts.length > 1) return { color: parts[0], size: parts[1] };
    return { color: "", size: v }; // Fallback for single variant (usually size)
  };

  const origVar = parseVariant(originalProduct.variant);
  const replVar = parseVariant(replacementInfoFromReturn?.variant);

  const displayReplacement = {
    image: replacementProduct?.image || replacementInfoFromReturn?.image || originalProduct.image,
    title: replacementProduct?.title || replacementInfoFromReturn?.title || originalProduct.title,
    color: replacementProduct?.color || replVar.color || origVar.color,
    size: replacementProduct?.size || replVar.size || origVar.size,
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="tw-min-h-screen tw-p-6 md:tw-p-10 tw-font-sans">
      {/* Top Breadcrumb & Actions */}
      <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-justify-between tw-pb-8 tw-gap-4">
        <div className="tw-space-y-3">
          <div className="tw-text-xs tw-font-semibold tw-tracking-widest tw-uppercase tw-text-gray-500">
            <button onClick={goBack} className="hover:tw-text-gray-300 tw-transition-colors tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer tw-text-inherit tw-font-inherit tw-uppercase">ORDERS</button>
            <span className="tw-mx-1">&gt;</span> 
            <button onClick={() => navigate(`/customer/orders/${orderId}`)} className="hover:tw-text-gray-300 tw-transition-colors tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer tw-text-inherit tw-font-inherit tw-uppercase">#{orderDetails?.order_number}</button>
            <span className="tw-mx-1">&gt;</span> 
            <span>{latestReturn?.category === 'fix' ? 'FIT ISSUE' : latestReturn?.category || 'RETURN'}</span>
            <span className="tw-mx-1">&gt;</span> 
            <span className="tw-text-[#00C5FB]">TRACK ORDER</span>
          </div>
          <h1 className="tw-text-3xl md:tw-text-4xl tw-font-bold tw-text-white tw-tracking-tight">
            Order #{orderDetails?.order_number}
          </h1>
          <div className="tw-flex tw-items-center tw-gap-4 tw-mt-2">
            <div className="tw-flex tw-items-center tw-gap-1.5 tw-bg-[#1E1E1E] tw-text-[#00C5FB] tw-px-2.5 tw-py-1 tw-rounded-md tw-text-xs tw-font-bold tw-tracking-wide">
              {latestReturn?.id ? `CLAIM: #${latestReturn.id.split("-")[0].toUpperCase()}` : "PROCESSING"}
            </div>
            {latestReturn?.created_at && (
              <span className="tw-text-sm tw-text-gray-400 tw-font-medium">
                Initiated on {new Date(latestReturn.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              </span>
            )}
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-gap-3">
          {/* Sign Out removed as per request */}
        </div>
      </div>

      <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-6">
        <div className="tw-flex-1 tw-space-y-5">

          {/* Product Comparison */}
          <div className="tw-bg-black tw-rounded-2xl tw-p-4 sm:tw-p-5 tw-border tw-border-gray-800/50">
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-stretch tw-gap-3">
              {/* Returning */}
              <div className="tw-bg-[#252528] tw-rounded-xl tw-p-4 tw-flex-1 tw-flex tw-flex-col">
                <p className="tw-text-[10px] tw-text-yellow-400 tw-font-bold tw-uppercase tw-tracking-wider tw-mb-3">Returning Product</p>
                <div className="tw-flex tw-items-center tw-gap-3 tw-flex-1">
                  {originalProduct.image && (
                    <img src={originalProduct.image} alt="returning" className="tw-w-14 tw-h-14 tw-rounded-lg tw-object-cover tw-flex-shrink-0" />
                  )}
                  <div>
                    <p className="tw-text-white tw-font-semibold tw-text-sm">{originalProduct.title}</p>
                    <p className="tw-text-gray-400 tw-text-xs tw-mt-0.5">
                      {originalProduct.variant}
                    </p>
                  </div>
                </div>
                <div className="tw-mt-4">
                  <span className="tw-bg-red-500/10 tw-text-red-400 tw-text-[9px] tw-px-2.5 tw-py-1.5 tw-rounded-md tw-uppercase tw-font-bold tw-tracking-wide tw-border tw-border-red-500/30 tw-inline-block">
                    {originalProduct.reason}
                  </span>
                </div>
              </div>

              {/* Arrow */}
              <div className="tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-[#333] tw-flex tw-items-center tw-justify-center">
                  <ArrowRight className="tw-w-4 tw-h-4 tw-text-white" />
                </div>
              </div>
              
              {/* Replacement or Refund */}
              <div className="tw-bg-[#252528] tw-rounded-xl tw-p-4 tw-flex-1 tw-flex tw-flex-col">
                <p className={`tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-mb-3 ${
                  latestReturn?.return_type?.startsWith('refund') || latestReturn?.return_type === 'store_credit' 
                    ? "tw-text-green-400" 
                    : "tw-text-[#00C5FB]"
                }`}>
                  {latestReturn?.return_type?.startsWith('refund') ? "Refund Resolution" : 
                   latestReturn?.return_type === 'store_credit' ? "Store Credit Resolution" : 
                   "Replacement Product"}
                </p>
                
                {latestReturn?.return_type?.startsWith('refund') || latestReturn?.return_type === 'store_credit' ? (
                  <div className="tw-flex tw-items-center tw-gap-4 tw-flex-1">
                    <div className="tw-w-14 tw-h-14 tw-rounded-lg tw-bg-[#1E1E1E] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-border tw-border-white/5">
                      <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-green-500/20 tw-flex tw-items-center tw-justify-center">
                        <Check className="tw-w-5 tw-h-5 tw-text-green-500" />
                      </div>
                    </div>
                    <div>
                      <p className="tw-text-white tw-font-semibold tw-text-sm">
                        {latestReturn?.return_type?.startsWith('refund') ? "Full Refund" : "Store Credit"}
                      </p>
                      <p className="tw-text-gray-400 tw-text-xs tw-mt-0.5">
                        {latestReturn?.return_type?.startsWith('refund') ? "To original payment method" : "Added to your wallet"}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="tw-flex tw-items-center tw-gap-3 tw-flex-1">
                    {displayReplacement.image && (
                      <img src={displayReplacement.image} alt="replacement" className="tw-w-14 tw-h-14 tw-rounded-lg tw-object-cover tw-flex-shrink-0" />
                    )}
                    <div>
                      <p className="tw-text-white tw-font-semibold tw-text-sm">{displayReplacement.title}</p>
                      <p className="tw-text-gray-400 tw-text-xs tw-mt-0.5">
                        {replacementInfoFromReturn?.variant || originalProduct.variant}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="tw-mt-4">
                  <span className={`tw-text-[9px] tw-font-bold tw-inline-flex tw-items-center tw-gap-1.5 tw-uppercase tw-tracking-wide tw-px-2.5 tw-py-1.5 tw-rounded-md tw-border ${
                    latestReturn?.return_type?.startsWith('refund') || latestReturn?.return_type === 'store_credit'
                      ? "tw-text-green-400 tw-bg-green-500/10 tw-border-green-500/30"
                      : "tw-text-[#00C5FB] tw-bg-[#00C5FB]/10 tw-border-[#00C5FB]/30"
                  }`}>
                    <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full tw-inline-block ${
                      latestReturn?.return_type?.startsWith('refund') || latestReturn?.return_type === 'store_credit'
                        ? "tw-bg-green-500"
                        : "tw-bg-[#00C5FB]"
                    }`} /> PROCESSING
                  </span>
                </div>
              </div>
            </div>
          </div>
 
          {/* Status Confirmed */}
          <div className="tw-bg-black tw-rounded-2xl tw-p-5 tw-border tw-border-gray-800/50">
            <div className="tw-flex tw-items-start tw-gap-4 tw-mb-4">
              <div className={`tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${
                latestReturn?.return_type?.startsWith('refund') || latestReturn?.return_type === 'store_credit'
                  ? "tw-bg-green-500"
                  : "tw-bg-[#00C5FB]"
              }`}
                style={{ boxShadow: `0 0-12px 3px ${latestReturn?.return_type?.startsWith('refund') || latestReturn?.return_type === 'store_credit' ? 'rgba(34, 197, 94, 0.4)' : 'rgba(0, 197, 251, 0.4)'}` }}>
                <Check className="tw-w-5 tw-h-5 tw-text-black" strokeWidth={3} />
              </div>
              <div>
                <p className="tw-text-white tw-font-bold tw-text-base">
                  {latestReturn?.return_type?.startsWith('refund') ? "Refund Confirmed" : 
                   latestReturn?.return_type === 'store_credit' ? "Store Credit Confirmed" : 
                   "Exchange Confirmed"}
                </p>
                <p className="tw-text-gray-400 tw-text-sm tw-mt-0.5">
                  {latestReturn?.return_type?.startsWith('refund') || latestReturn?.return_type === 'store_credit'
                    ? "The product will be picked up first. Once the pickup is verified, your refund will be processed to the selected method."
                    : "The damaged product will be picked up first. Once the pickup is verified, your replacement product will be dispatched immediately."}
                </p>
              </div>
            </div>
            <div className="tw-bg-[#131313] tw-rounded-xl tw-p-4 tw-flex tw-items-center tw-gap-4 tw-w-full tw-border tw-border-white/5">
              <div className={`tw-w-10 tw-h-10 tw-rounded-xl tw-flex tw-items-center tw-justify-center ${
                latestReturn?.return_type?.startsWith('refund') || latestReturn?.return_type === 'store_credit'
                  ? "tw-bg-green-500/10"
                  : "tw-bg-[#00C5FB]/10"
              }`}>
                <Calendar className={`tw-w-5 tw-h-5 ${
                  latestReturn?.return_type?.startsWith('refund') || latestReturn?.return_type === 'store_credit'
                    ? "tw-text-green-500"
                    : "tw-text-[#00C5FB]"
                }`} />
              </div>
              <div>
                <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-widest tw-font-bold">Estimated Pickup</p>
                <p className="tw-text-white tw-font-bold tw-text-sm">
                  {latestReturn?.courier_metadata?.pickup_scheduled_at 
                    ? new Date(latestReturn.courier_metadata.pickup_scheduled_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
                    : "Awaiting Schedule"}
                </p>
              </div>
            </div>
          </div>

          {/* Pickup Details */}
          <div className="tw-bg-black tw-rounded-2xl tw-p-6 tw-border tw-border-gray-800/50">
            <div className="tw-flex tw-justify-between tw-items-start tw-mb-6">
              <div>
                <h3 className="tw-text-white tw-font-bold tw-text-lg">Logistics Details</h3>
                <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
                  <span className={`tw-w-2 tw-h-2 tw-rounded-full ${latestReturn?.courier_metadata?.pickup_status === 'scheduled' ? 'tw-bg-green-500' : 'tw-bg-yellow-500'}`} />
                  <p className="tw-text-gray-400 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider">
                    {latestReturn?.courier_metadata?.pickup_status || "Processing"}
                  </p>
                </div>
              </div>
              <Truck className="tw-w-6 tw-h-6 tw-text-[#00C5FB]" />
            </div>
            
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-6 tw-mb-6">
              <div className="tw-space-y-1">
                <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-widest tw-font-bold">Courier Partner</p>
                <p className="tw-text-white tw-font-semibold">{latestReturn?.courier_metadata?.courier_name || "Assigning..."}</p>
              </div>
              <div className="tw-space-y-1">
                <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-widest tw-font-bold">Tracking ID (AWB)</p>
                {latestReturn?.courier_metadata?.awb_code ? (
                  <a 
                    href={latestReturn.courier_metadata.tracking_url || `https://shiprocket.co/tracking/${latestReturn.courier_metadata.awb_code}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tw-text-[#00C5FB] tw-font-mono tw-font-bold hover:tw-underline tw-flex tw-items-center tw-gap-1"
                  >
                    {latestReturn.courier_metadata.awb_code}
                    <ExternalLink className="tw-w-3 tw-h-3" />
                  </a>
                ) : (
                  <p className="tw-text-gray-500 tw-font-bold">Pending</p>
                )}
              </div>
              <div className="tw-space-y-1">
                <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-widest tw-font-bold">Contact Name</p>
                <p className="tw-text-white tw-font-semibold">{shipping?.name || "—"}</p>
              </div>
              <div className="tw-space-y-1">
                <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-widest tw-font-bold">Contact Phone</p>
                <p className="tw-text-white tw-font-semibold">{shipping?.phone || "—"}</p>
              </div>
              <div className="tw-col-span-1 sm:tw-col-span-2 tw-space-y-1">
                <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-widest tw-font-bold">Pickup Address</p>
                {shipping ? (
                  <p className="tw-text-white tw-text-sm tw-leading-relaxed">{shipping.address_line_1}{shipping.address_line_2 ? `, ${shipping.address_line_2}` : ""}, {shipping.city}, {shipping.state} {shipping.zip}</p>
                ) : <p className="tw-text-gray-500">—</p>}
              </div>
            </div>

            <div className="tw-bg-[#0F0F0F] tw-rounded-2xl tw-p-4 tw-flex tw-items-start tw-gap-3 tw-border tw-border-white/5">
              <AlertCircle className="tw-w-4 tw-h-4 tw-text-[#00C5FB] tw-flex-shrink-0 tw-mt-0.5" />
              <p className="tw-text-gray-400 tw-text-xs tw-leading-relaxed">
                Please keep the damaged item packed and ready. Ensure the original tags are intact. Our courier partner will verify the item before pickup.
              </p>
            </div>
          </div>

          {/* Replacement Dispatch Details */}
          {latestReturn?.reorder_id && (
            <div className="tw-bg-black tw-rounded-2xl tw-p-6 tw-border tw-border-gray-800/50">
              <div className="tw-flex tw-justify-between tw-items-start tw-mb-6">
                <div>
                  <h3 className="tw-text-white tw-font-bold tw-text-lg">Replacement Dispatch</h3>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
                    <span className={`tw-w-2 tw-h-2 tw-rounded-full ${latestReturn?.reorder_status === 'shipped' ? 'tw-bg-green-500' : 'tw-bg-blue-500'}`} />
                    <p className="tw-text-gray-400 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider">
                      {latestReturn?.reorder_status === 'created' ? 'Awaiting Dispatch' : latestReturn?.reorder_status || "Processing"}
                    </p>
                  </div>
                </div>
                <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-[#00C5FB]/10 tw-flex tw-items-center tw-justify-center">
                   <Truck className="tw-w-6 tw-h-6 tw-text-[#00C5FB]" />
                </div>
              </div>

              <div className="tw-space-y-4">
                <div className="tw-bg-[#0F0F0F] tw-rounded-xl tw-p-4 tw-border tw-border-white/5">
                  <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-widest tw-font-bold tw-mb-1">New Order Reference</p>
                  <p className="tw-text-white tw-font-mono tw-font-bold tw-text-sm">REORDER-{latestReturn.reorder_id.split("-")[0].toUpperCase()}</p>
                </div>
                
                <p className="tw-text-gray-400 tw-text-xs tw-leading-relaxed">
                  Your replacement product is being prepared for dispatch. You will receive a new tracking number as soon as it's handed over to our courier partner.
                </p>
              </div>
            </div>
          )}
        </div>
        <TrackingPanel />
      </div>
    </motion.div>
  );
};

export default TrackStatus;
